import React from 'react';
import Icon from '../AppIcon';

const FormNavigation = ({ 
  onPrevious, 
  onNext, 
  onSave,
  canGoBack = false,
  canGoNext = true,
  isLastStep = false,
  isLoading = false,
  validationErrors = [],
  currentSection = "Introduction"
}) => {
  const handleNext = () => {
    if (canGoNext && !isLoading && onNext) {
      onNext();
    }
  };

  const handlePrevious = () => {
    if (canGoBack && !isLoading && onPrevious) {
      onPrevious();
    }
  };

  const handleSave = () => {
    if (onSave && !isLoading) {
      onSave();
    }
  };

  return (
    <>
      {/* Desktop Navigation */}
      <div className="hidden md:block fixed right-8 top-1/2 transform -translate-y-1/2 z-40">
        <div className="bg-surface rounded-xl shadow-elevated border border-border p-4 space-y-4 w-64">
          <div className="text-center">
            <h3 className="font-heading font-medium text-text-primary mb-1">
              Current Section
            </h3>
            <p className="text-sm text-text-secondary">{currentSection}</p>
          </div>

          {validationErrors.length > 0 && (
            <div className="bg-error-50 border border-error-100 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <Icon name="AlertCircle" size={16} color="#EF4444" className="mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-error mb-1">
                    Please complete required fields
                  </p>
                  <ul className="text-xs text-error space-y-1">
                    {validationErrors.slice(0, 3).map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                    {validationErrors.length > 3 && (
                      <li>• And {validationErrors.length - 3} more...</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-surface-secondary border border-border rounded-lg text-text-secondary hover:text-text-primary hover:border-border-focus transition-all duration-150 disabled:opacity-50"
            >
              <Icon name="Save" size={16} />
              <span className="text-sm font-medium">Save Progress</span>
            </button>

            {canGoBack && (
              <button
                onClick={handlePrevious}
                disabled={isLoading}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-surface border border-border rounded-lg text-text-primary hover:bg-surface-secondary transition-all duration-150 disabled:opacity-50"
              >
                <Icon name="ChevronLeft" size={16} />
                <span className="text-sm font-medium">Previous</span>
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={!canGoNext || isLoading || validationErrors.length > 0}
              className={`
                w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                ${isLastStep 
                  ? 'bg-success text-white hover:bg-success-700 focus:ring-success' :'bg-primary text-white hover:bg-primary-700 focus:ring-primary'
                } focus:outline-none focus:ring-2 focus:ring-offset-2
              `}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  <span className="text-sm">Processing...</span>
                </>
              ) : (
                <>
                  <span className="text-sm">
                    {isLastStep ? 'Complete Assessment' : 'Continue'}
                  </span>
                  <Icon name={isLastStep ? "Check" : "ChevronRight"} size={16} />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-border z-50">
        <div className="px-4 py-4 space-y-3">
          {validationErrors.length > 0 && (
            <div className="bg-error-50 border border-error-100 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} color="#EF4444" />
                <p className="text-sm font-medium text-error">
                  {validationErrors.length} field{validationErrors.length > 1 ? 's' : ''} need attention
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center justify-center p-3 bg-surface-secondary border border-border rounded-lg text-text-secondary hover:text-text-primary transition-all duration-150 disabled:opacity-50"
            >
              <Icon name="Save" size={20} />
            </button>

            {canGoBack && (
              <button
                onClick={handlePrevious}
                disabled={isLoading}
                className="flex items-center justify-center px-6 py-3 bg-surface border border-border rounded-lg text-text-primary hover:bg-surface-secondary transition-all duration-150 disabled:opacity-50"
              >
                <Icon name="ChevronLeft" size={20} className="mr-2" />
                <span className="font-medium">Back</span>
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={!canGoNext || isLoading || validationErrors.length > 0}
              className={`
                flex-1 flex items-center justify-center px-6 py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                ${isLastStep 
                  ? 'bg-success text-white hover:bg-success-700' :'bg-primary text-white hover:bg-primary-700'
                }
              `}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>{isLastStep ? 'Complete' : 'Continue'}</span>
                  <Icon name={isLastStep ? "Check" : "ChevronRight"} size={20} className="ml-2" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormNavigation;